import { useState } from 'react';
import { CheckCircle2, Loader2, Play, XCircle } from 'lucide-react';

import { useRuns, useTriggerRun } from '../hooks';

export function RunsView() {
  const { runs, loading, error, refetch } = useRuns(20);
  const { trigger, loading: triggering, error: triggerError } = useTriggerRun();
  const [sinceDays, setSinceDays] = useState(30);
  const [lastResult, setLastResult] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Corridas de scraping</h1>
          <p className="text-xs text-white/50">
            Cada lunes 13:00 UTC corre automáticamente. También puedes disparar una ahora.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col text-xs">
            <span className="mb-1 text-white/50">Últimos N días</span>
            <input
              type="number"
              min={1}
              max={365}
              value={sinceDays}
              onChange={(e) => setSinceDays(Number(e.target.value))}
              className="input-base w-24"
            />
          </label>
          <button
            type="button"
            disabled={triggering}
            onClick={async () => {
              setLastResult(null);
              try {
                const r = await trigger(sinceDays);
                setLastResult(
                  `Corrida completa: ${r.raw_rows} rows raw → ${r.new_projects} nuevos proyectos → ${r.leads} leads creados.`,
                );
                refetch();
              } catch {
                /* error capturado en hook */
              }
            }}
            className="o-btn-ghost inline-flex items-center gap-1 !bg-gradient-to-b !from-emerald-500/30 !to-emerald-500/15 text-sm font-medium text-emerald-200 hover:!from-emerald-500/40 disabled:opacity-40"
          >
            {triggering ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Correr ahora
          </button>
        </div>
      </header>

      {triggerError ? (
        <div className="o-card border-red-500/30 text-sm text-red-300">{triggerError}</div>
      ) : null}
      {lastResult ? (
        <div className="o-card border-emerald-500/30 text-sm text-emerald-200">{lastResult}</div>
      ) : null}
      {error ? <div className="o-card border-red-500/30 text-sm text-red-300">{error}</div> : null}

      <div className="o-card overflow-x-auto !p-0">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-white/50">
            <tr className="border-b border-white/10">
              <th className="px-3 py-3 text-left">Estatus</th>
              <th className="px-3 py-3 text-left">Inicio</th>
              <th className="px-3 py-3 text-left">Fin</th>
              <th className="px-3 py-3 text-right">Proyectos</th>
              <th className="px-3 py-3 text-right">Leads</th>
              <th className="px-3 py-3 text-left">Error</th>
            </tr>
          </thead>
          <tbody>
            {loading && runs.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-white/40">Cargando...</td></tr>
            ) : runs.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-white/40">Sin corridas todavía.</td></tr>
            ) : (
              runs.map((r) => (
                <tr key={r.id} className="border-t border-white/5 text-white/80">
                  <td className="px-3 py-2">
                    {r.status === 'completed' ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={12} /> {r.status}</span>
                    ) : r.status === 'failed' ? (
                      <span className="inline-flex items-center gap-1 text-red-300"><XCircle size={12} /> {r.status}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-300"><Loader2 size={12} className="animate-spin" /> {r.status}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-white/60">{r.started_at || '—'}</td>
                  <td className="px-3 py-2 text-xs text-white/60">{r.completed_at || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.projects_discovered}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-emerald-300">{r.leads_created}</td>
                  <td className="px-3 py-2 text-xs text-red-300">{r.error_message || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
